import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import './index.less';
import { computed, type Signal } from '@preact/signals';
import { For } from '@preact/signals/utils';
import type { NewsType, News as TypeNews } from '@pst/biz';
import { SlideArrowButton } from '@pst/components-ts';
import dayjs from 'dayjs';
import { usePaginationSwiper } from '../../hooks/usePaginationSwiper';
import { newsModule } from '../../services';
import { navToNewsDetail } from '../../utils/nav';
import { Spin } from '../Spin';

function groupByCount(newsList: TypeNews[]): TypeNews[][] {
  return newsList.reduce((acc, cur) => {
    const last = acc[acc.length - 1];
    if (last && last.length < 4) {
      last.push(cur);
    } else {
      acc.push([cur]);
    }
    return acc;
  }, [] as TypeNews[][]);
}

const loadingNews = computed(() => newsModule.store.loadingNews.value);
const loadingUpdates = computed(() => newsModule.store.loadingUpdates.value);

const latestNews = computed(() => newsModule.store.news.value[0]);
const othersNews = computed(() => groupByCount(newsModule.store.news.value.slice(1)));

const latestUpdates = computed(() => newsModule.store.updates.value[0]);
const othersUpdates = computed(() => groupByCount(newsModule.store.updates.value.slice(1)));

function View({
  latestItem,
  list,
  loading,
  newsType,
  visible,
}: {
  visible: boolean;
  latestItem: Signal<TypeNews>;
  list: Signal<TypeNews[][]>;
  loading: boolean;
  newsType: NewsType;
}) {
  const { emblaRef, scrollPrev, scrollNext, reInit, canScrollPrev, canScrollNext, emblaPaginationVisible } =
    usePaginationSwiper({
      loop: false,
    });

  useEffect(() => {
    // 当组件变为可见时，重新初始化轮播组件
    if (visible) {
      // 使用setTimeout确保DOM已经渲染完成
      const timer = setTimeout(() => {
        reInit();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [visible, reInit]);

  return (
    <div
      class="news-row"
      style={{
        display: visible ? undefined : 'none',
      }}
    >
      {!!loading && <Spin />}

      {latestItem.value && (
        <div
          class="news-card large"
          onClick={() => {
            navToNewsDetail(latestItem.value.id, newsType === newsModule.NewsType.News ? 'news' : 'updates');
          }}
        >
          <div class="news-card-img">
            <img src={latestItem.value.image} alt={latestItem.value.title} />
          </div>
          <div class="news-card-title">
            <div>{latestItem.value.title}</div>
            <div className="news-card-time">
              {dayjs(latestItem.value.update_at * 1000).format('YYYY-MM-DD')}
            </div>
          </div>
        </div>
      )}

      <div class="news-items embla">
        <div class="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            <For each={list}>
              {(newsGroup, groupIndex) => {
                return (
                  <div className="embla__slide news-slide" key={groupIndex}>
                    {newsGroup.map((newsItem, index) => {
                      return (
                        <div
                          class="news-card"
                          key={String(index)}
                          onClick={() => {
                            navToNewsDetail(
                              newsItem.id,
                              newsType === newsModule.NewsType.News ? 'news' : 'updates',
                            );
                          }}
                        >
                          <div class="news-card-img">
                            <img src={newsItem.image} alt="" loading="lazy" />
                          </div>
                          <div class="news-card-title">
                            <div>{newsItem.title}</div>
                            <div className="news-card-time">
                              {dayjs(newsItem.update_at * 1000).format('YYYY-MM-DD')}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            </For>
          </div>
        </div>

        <div
          class="news-pagination flex justify-center w-full"
          style={{ display: emblaPaginationVisible ? 'flex' : 'none' }}
        >
          <SlideArrowButton
            disabled={!canScrollPrev}
            direction="left"
            onClick={scrollPrev}
          ></SlideArrowButton>
          <SlideArrowButton
            disabled={!canScrollNext}
            direction="right"
            onClick={scrollNext}
          ></SlideArrowButton>
        </div>
      </div>
    </div>
  );
}

function News() {
  const [newsType, setNewsType] = useState(newsModule.NewsType.News);

  return (
    <div class="news-content">
      <div class="news-tab">
        <div
          class={`news-tab-item ${newsType === newsModule.NewsType.News ? 'news-tab-item-active' : ''}`}
          onClick={() => setNewsType(newsModule.NewsType.News)}
        >
          新闻
          {/* <img class="news-tab-title" src={titleUpdates} alt="" />
          <img class="news-tab-title-active" src={titleNews} alt="" /> */}
        </div>
        <div
          class={`news-tab-item ${newsType === newsModule.NewsType.Updates ? 'news-tab-item-active' : ''}`}
          onClick={() => setNewsType(newsModule.NewsType.Updates)}
        >
          公告
          {/* <img class="news-tab-title" src={titleUpdates} alt="" />
          <img class="news-tab-title-active" src={titleNews} alt="" /> */}
        </div>
      </div>

      <View
        visible={newsType === newsModule.NewsType.News}
        latestItem={latestNews}
        list={othersNews}
        loading={loadingNews.value}
        newsType={newsModule.NewsType.News}
      />

      <View
        visible={newsType === newsModule.NewsType.Updates}
        latestItem={latestUpdates}
        list={othersUpdates}
        loading={loadingUpdates.value}
        newsType={newsModule.NewsType.Updates}
      />
    </div>
  );
}

export function mountNews(el?: Element | null) {
  if (el) {
    render(<News />, el);
  }
}
